import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import PageHeader from '../shell/PageHeader';
import { SprayKpiRow }               from './SprayKpiRow';
import { SprayUpcomingSessionsCard } from './SprayUpcomingSessionsCard';
import { SprayPhiTrackerCard }       from './SprayPhiTrackerCard';
import { SprayAlertStack }           from './SprayAlertStack';
import { ChemicalStockUsageWidget }  from './ChemicalStockUsageWidget';
import { Banner }                    from '../patterns';

import { getSpraySummary, type SpraySummary } from '../../services/sprayDashboardService';
import { getTenantInfo }                       from '../../services/tenantService';
import { computeSprayKpis, classifyLicenseExpiry, classifyCalibrationDue } from '../../utils/sprayDashboardMath';

import './SprayDashboard.css';

const HORIZON_DAYS = 30;

export const SprayDashboard: React.FC = () => {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  const [summary,        setSummary]        = useState<SpraySummary | null>(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState<string | null>(null);
  const [hasFarmLocation, setHasFarmLocation] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, tenant] = await Promise.all([getSpraySummary(), getTenantInfo()]);
        if (cancelled) return;
        setSummary(data);
        const coords = tenant?.centralLocation?.coordinates;
        setHasFarmLocation(Array.isArray(coords) && coords.length === 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number');
      } catch (err: any) {
        Sentry.captureException(err);
        if (!cancelled) setError(err?.message ?? 'load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const now     = useMemo(() => new Date(), []);
  const horizon = useMemo(() => new Date(now.getTime() + HORIZON_DAYS * 86400e3), [now]);

  const kpis         = useMemo(() => summary ? computeSprayKpis(summary, now) : null, [summary, now]);
  const licenses     = useMemo(() => summary ? classifyLicenseExpiry(summary.operatorLicenses,    now) : [], [summary, now]);
  const calibrations = useMemo(() => summary ? classifyCalibrationDue(summary.machineCalibrations, now) : [], [summary, now]);

  if (loading) {
    return (
      <div role="status" aria-label={t('spray.dashboard.loading')} style={{ padding: 24, textAlign: 'center' }}>
        {t('spray.dashboard.loading')}
      </div>
    );
  }

  if (error || !summary || !kpis) {
    return (
      <div role="alert" style={{ padding: 16, color: '#B91C1C', border: '1px solid #FECACA', borderRadius: 6, margin: 16 }}>
        {t('spray.dashboard.error')}
      </div>
    );
  }

  if (hasFarmLocation === false) {
    return (
      <div data-testid="spray-dashboard-farm-location-blocker" style={{ padding: 16 }}>
        <Banner
          tone="offline"
          title={t('spray.dashboard.farmLocationRequired.title')}
          detail={t('spray.dashboard.farmLocationRequired.body')}
          actions={
            <Link to="/settings/general" className="pat-btn pat-btn--primary">
              {t('spray.dashboard.farmLocationRequired.cta')}
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div data-testid="spray-dashboard" className="spray-dashboard">
      <PageHeader
        title={t('nav.spraying')}
        meta={t('spray.dashboard.meta', { upcoming: summary.upcomingSessions.length })}
        actions={
          <>
            <button type="button" className="pat-btn pat-btn--subtle"  onClick={() => navigate('/protection/programs/new')}>
              {t('spray.dashboard.quickActions.createProgram')}
            </button>
            <button type="button" className="pat-btn pat-btn--outline" onClick={() => navigate('/protection/stock-levels')}>
              {t('spray.dashboard.quickActions.manageStock')}
            </button>
            <button type="button" className="pat-btn pat-btn--primary" onClick={() => navigate('/protection/sessions/new')}>
              {t('spray.dashboard.quickActions.logSession')}
            </button>
          </>
        }
      />

      <SprayKpiRow kpis={kpis} />

      <div className="spray-dashboard__body">
        <div className="spray-dashboard__body-left">
          <SprayUpcomingSessionsCard sessions={summary.upcomingSessions} />
          <SprayPhiTrackerCard       rows={summary.phiTracker} horizon={horizon} />
        </div>
        <div className="spray-dashboard__body-right">
          <SprayAlertStack
            longRunning={summary.activePrograms.longRunningSessions}
            licenses={licenses}
            calibrations={calibrations}
          />
        </div>
      </div>

      <ChemicalStockUsageWidget rows={summary.chemicalStockUsage} />
    </div>
  );
};

export default SprayDashboard;
